/**
 * Resellers Management
 *
 * This module provides functionality for managing resellers, including creating, updating, and deleting resellers.
 * It includes endpoints for creating resellers, updating their details, and deleting them.
 *
 * @module ResellersManagement
 */

import { api } from './axios'
import type {
  CreateResellerDTO,
  CreateResellerResponse,
  GetResellerResponse,
  GetResellersResponse,
  ResellerFilters,
  ResellerResponseDto,
  UpdateResellerDTO,
} from './types'

/**
 * Get all resellers with optional filtering and pagination
 */
export const getResellers = async (filters?: ResellerFilters): Promise<GetResellersResponse> => {
  const params = new URLSearchParams()

  if (filters?.status) params.append('status', filters.status)
  if (filters?.search) params.append('search', filters.search)
  if (filters?.limit) params.append('limit', filters.limit.toString())
  if (filters?.offset) params.append('offset', filters.offset.toString())

  const queryString = params.toString()
  const url = queryString ? `/resellers?${queryString}` : '/resellers'

  const response = await api.get<GetResellersResponse>(url)

  return response.data
}

/**
 * Get a specific reseller by ID
 */
export const getReseller = async (id: string): Promise<GetResellerResponse> => {
  const response = await api.get<GetResellerResponse>(`/resellers/${id}`)

  return response.data
}

/**
 * Create a new reseller
 */
export const createReseller = async (data: CreateResellerDTO): Promise<CreateResellerResponse> => {
  const response = await api.post<CreateResellerResponse>('/resellers', data)

  return response.data
}

/**
 * Update an existing reseller
 */
export const updateReseller = async (
  id: string,
  data: UpdateResellerDTO,
): Promise<ResellerResponseDto> => {
  const response = await api.patch<ResellerResponseDto>(`/resellers/${id}`, data)

  return response.data
}

/**
 * Delete a reseller
 */
export const deleteReseller = async (id: string): Promise<void> => {
  await api.delete(`/resellers/${id}`)
}

/**
 * Activate a reseller
 */
export const activateReseller = async (id: string): Promise<ResellerResponseDto> => {
  const response = await api.post<ResellerResponseDto>(`/resellers/${id}/activate`)

  return response.data
}

/**
 * Deactivate a reseller
 */
export const deactivateReseller = async (id: string): Promise<ResellerResponseDto> => {
  const response = await api.patch<ResellerResponseDto>(`/resellers/${id}/deactivate`)

  return response.data
}

/**
 * Suspend a reseller
 */
export const suspendReseller = async (id: string): Promise<ResellerResponseDto> => {
  const response = await api.post<ResellerResponseDto>(`/resellers/${id}/suspend`)

  return response.data
}

/**
 * Get reseller statistics
 */
export const getResellerStats = async () => {
  const response = await api.get('/resellers/stats')

  return response.data
}

/**
 * Get reseller usage and quota information
 */
export const getResellerUsage = async (id: string) => {
  const response = await api.get(`/resellers/${id}/usage`)

  return response.data
}

/**
 * Update reseller billing settings
 */
export const updateResellerBilling = async (
  id: string,
  data: {
    isBillingManaged: boolean
    storagePricePerGbCents?: number
    apiCallPriceCents?: number
  },
) => {
  const response = await api.patch(`/resellers/${id}/billing`, data)

  return response.data
}
